import { CSG } from "@babylonjs/core/Meshes/csg";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { GeoUtils } from './GeoUtils';
import { OpeningGeometry } from './OpeningGeometry';
import { RoomManager } from './../../RoomManager';
import { Util } from '../../utils/Util';
import { ElementChangedFlags } from '@egr/wcf/modules/core/mdl';
import { PropertyValue, NumberProperty, LengthProperty, PropertyChangedResult, BoolProperty } from '@egr/wcf/modules/core/prop';
import { clamp, eq } from '@egr/wcf/modules/utils/math';
export class Window extends OpeningGeometry {
    constructor(pRoomManager, pOpening) {
        super(pRoomManager, pOpening);
        this.mOpening = pOpening;
        this.mOpening.OpeningParams.privacyFilter = false;
        this.mOpening.OpeningParams.blindVisible = false;
        this.mOpening.OpeningParams.blindDepth = 0.07;
        this.mOpening.OpeningParams.blindOpeningAngle = 60;
        this.mOpening.OpeningParams.blindOpeningWidth = 0;
        this.mOpening.OpeningParams.ColumnCount = 2;
        this.mOpening.OpeningParams.minWidth = 0.6;
        this.mOpening.OpeningParams.windowSill = true;
        this.mOpening.OpeningParams.glazingBars = false;
        this.mOpening.OpeningParams.glazingBars_width = 0.026;
        this.mOpening.OpeningParams.glazingBars_h_segments = 1;
        this.mOpening.OpeningParams.glazingBars_v_segments = 1;
        this.mGlazingBarsWidth_Prop = new PropertyValue(this.mOpening.OpeningParams.glazingBars_width);
        this.mGlazingBarsHorizontal_Prop = new PropertyValue(this.mOpening.OpeningParams.glazingBars_h_segments);
        this.mGlazingBarsVertical_Prop = new PropertyValue(this.mOpening.OpeningParams.glazingBars_v_segments);
    }
    getType() {
        return ("Window");
    }
    get canMirrorV() {
        if (this.mOpening.OpeningParams.ColumnCount == 2)
            return (false);
        return (true);
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = 1.5;
        if (this.mOpening.OpeningParams.ColumnCount == 1) {
            tParams.width = 1.2;
        }
        tParams.height = 1.6;
        tParams.floorOffset = 0.8;
        return (tParams);
    }
    create2dGeometry(pParent) {
        let tHW = this.mOpening.width;
        let tPO = -tHW * 0.5; // point of origin ("Nullpunkt"; links)
        let tSW = tHW * (1 / this.mOpening.OpeningParams.ColumnCount); // segment width
        for (let i = 1; i <= this.mOpening.OpeningParams.ColumnCount; i++) {
            let tSP = tPO + tSW * (i - 1);
            let tFrameHD = 0.035;
            let tFrameW = 0.09;
            let tHandlePoints = [
                new Vector3(tSP + tSW - tFrameW * 0.5, 0.0, -tFrameHD),
                new Vector3(tSP + tSW - tFrameW * 0.5, 0.0, -tFrameHD - 0.03),
                new Vector3((tSP + tSW - tFrameW * 0.5) - 0.1, 0.0, -tFrameHD - 0.03)
            ];
            if (i == 2) {
                tHandlePoints = [
                    new Vector3(tSP + tFrameW * 0.5, 0.0, -tFrameHD),
                    new Vector3(tSP + tFrameW * 0.5, 0.0, -tFrameHD - 0.03),
                    new Vector3((tSP + tFrameW * 0.5) + 0.1, 0.0, -tFrameHD - 0.03)
                ];
            }
            if (this.mOpening.mirrorVertical) {
                for (let tPoint of tHandlePoints)
                    tPoint.x = -tPoint.x;
            }
            if (this.mOpening.mirrorHorizontal) {
                for (let tPoint of tHandlePoints)
                    tPoint.z = -tPoint.z;
            }
            let tLines = CreateLineSystem("", {
                lines: [
                    //outer frame 1
                    [
                        new Vector3(tSP, 0.0, -tFrameHD),
                        new Vector3(tSP, 0.0, tFrameHD),
                        new Vector3(tSP + tFrameW, 0.0, tFrameHD),
                        new Vector3(tSP + tFrameW, 0.0, -tFrameHD),
                        new Vector3(tSP, 0.0, -tFrameHD)
                    ],
                    //outer frame 2
                    [
                        new Vector3(tSP + tSW - tFrameW, 0.0, -tFrameHD),
                        new Vector3(tSP + tSW - tFrameW, 0.0, tFrameHD),
                        new Vector3(tSP + tSW, 0.0, tFrameHD),
                        new Vector3(tSP + tSW, 0.0, -tFrameHD),
                        new Vector3(tSP + tSW - tFrameW, 0.0, -tFrameHD)
                    ],
                    //window pane
                    [
                        new Vector3(tSP + tFrameW, 0.0, 0.0),
                        new Vector3(tSP + tSW - tFrameW, 0.0, 0.0)
                    ],
                    //handle
                    tHandlePoints
                ],
                updatable: false,
                instance: null
            }, this.mScene);
            tLines.isPickable = false;
            tLines.intersectionThreshold = 0;
            tLines.color = Color3.Black();
            tLines.position.y = RoomManager.WALLOPENING_2D_LINE_HEIGHT;
            tLines.parent = pParent;
            this.prepare2dMesh(tLines);
        }
    }
    create3dGeometry(pParent) {
        if (this.mOpening.OpeningParams.windowSill) {
            Window.SILL_HEIGHT = 0.04;
            this.createSill(pParent);
        }
        else {
            Window.SILL_HEIGHT = 0;
        }
        for (let tI = 1; tI <= this.mOpening.OpeningParams.ColumnCount; tI++) {
            let tFrame = this.createFrame(pParent, tI);
            this.createSash(tFrame, tI);
            if (this.mOpening.OpeningParams.glazingBars) {
                const tGBarH = this.createGlazingBarsH(tFrame);
                const tGBarV = this.createGlazingBarsV(tFrame);
                if (tGBarH != null && tGBarV != null) {
                    const tHCSG = CSG.FromMesh(tGBarH);
                    const tVCSG = CSG.FromMesh(tGBarV);
                    const tunionedBars = tHCSG.union(tVCSG);
                    const tglBars = tunionedBars.toMesh("glazingBars", null, this.mScene);
                    tGBarH.dispose();
                    tGBarV.dispose();
                    tglBars.parent = tFrame;
                    tglBars.material = this.mOpening.getMaterialofSet(this.mOpening.MaterialSet_Sash);
                    if (this.mOpening.wall != null) {
                        tglBars.name = "room:wall|" + this.mOpening.wall.id.toString();
                    }
                    this.prepare3dMesh(tglBars);
                }
            }
        }
        this.createBlind(pParent, this.mOpening.OpeningParams);
    }
    setWindowSegments(pSegments, pBlindParams) {
        let tValue = clamp(pSegments, 1, 10.0);
        if (eq(pBlindParams.ColumnCount, tValue))
            return (false);
        pBlindParams.ColumnCount = Math.trunc(tValue);
        this.mOpening.beginChange();
        this.mOpening.updateGeometry();
        this.mOpening.notifyChange(ElementChangedFlags.PropertyValues);
        this.mOpening.endChange();
        return (true);
    }
    getPropertyClasses() {
        return (Promise.resolve(this.mRoomManager.PropClasses));
    }
    getProperties() {
        let tProperties = new Array();
        //window segment number
        let tBlindSegmentsProp = new NumberProperty(this.mOpening, Window.PROP_KEY_WINDOWSEGMENTS, this.mRoomManager.resources.propertySegments);
        tBlindSegmentsProp.minValue = 1;
        tBlindSegmentsProp.maxValue = 10;
        tBlindSegmentsProp.numDecDigits = 0;
        tBlindSegmentsProp.numDigits = 2;
        tBlindSegmentsProp.getValue = () => new PropertyValue(this.mOpening.OpeningParams.ColumnCount);
        tBlindSegmentsProp.setValue = (pValue) => {
            let tValue = LengthProperty.ConvertValueToNumber(pValue);
            if (tValue != null && this.setSegments(tValue, this.mOpening.OpeningParams))
                return (Promise.resolve(PropertyChangedResult.Value));
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tBlindSegmentsProp);
        let tYesNoChoices = new Array();
        for (let tYesNo of this.mRoomManager._YesNo)
            tYesNoChoices.push(this.mRoomManager._getYesNoPropValue(tYesNo));
        //set sill
        let tSillProp = new BoolProperty(this.mOpening, Window.PROP_KEY_SILL, this.mRoomManager.resources.propertyWindowSill);
        tSillProp.choiceList = true;
        tSillProp.getChoices = () => Promise.resolve(tYesNoChoices);
        tSillProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mOpening.OpeningParams.windowSill);
        tSillProp.setValue = (pValue) => {
            if (pValue != null) {
                let tStr = BoolProperty.ConvertValueToBool(pValue);
                this.setWindowSill(tStr, this.mOpening.OpeningParams);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tSillProp);
        //set blind
        let tBlindProp = new BoolProperty(this.mOpening, Window.PROP_KEY_BLIND, this.mRoomManager.resources.propertyBlind);
        tBlindProp.choiceList = true;
        tBlindProp.getChoices = () => Promise.resolve(tYesNoChoices);
        tBlindProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mOpening.OpeningParams.blindVisible);
        tBlindProp.setValue = (pValue) => {
            if (pValue != null) {
                let tStr = BoolProperty.ConvertValueToBool(pValue);
                this.setBlind(tStr, this.mOpening.OpeningParams);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tBlindProp);
        //blind opening angle
        let tBlindOpeningAngleProp = new NumberProperty(this.mOpening, Window.PROP_KEY_BLINDOPENINGANGLE, this.mRoomManager.resources.propertyOpeningAngle);
        tBlindOpeningAngleProp.minValue = -90;
        tBlindOpeningAngleProp.maxValue = 90;
        tBlindOpeningAngleProp.numDecDigits = 0;
        tBlindOpeningAngleProp.numDigits = 2;
        tBlindOpeningAngleProp.visible = this.mOpening.OpeningParams.blindVisible;
        tBlindOpeningAngleProp.getValue = () => new PropertyValue(this.mOpening.OpeningParams.blindOpeningAngle);
        tBlindOpeningAngleProp.setValue = (pValue) => {
            let tValue = LengthProperty.ConvertValueToNumber(pValue);
            if (tValue != null && this.setBlindOpeningAngle(tValue, this.mOpening.OpeningParams))
                return (Promise.resolve(PropertyChangedResult.Value));
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tBlindOpeningAngleProp);
        //glazing bar
        let tGlazingBarsProp = new BoolProperty(this.mOpening, Window.PROP_KEY_GLAZINGBARS, this.mRoomManager.resources.propertyGlazingBars);
        tGlazingBarsProp.class = "glazingbars";
        tGlazingBarsProp.choiceList = true;
        tGlazingBarsProp.getChoices = () => Promise.resolve(tYesNoChoices);
        tGlazingBarsProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mOpening.OpeningParams.glazingBars);
        tGlazingBarsProp.setValue = (pValue) => {
            if (pValue != null) {
                let tValue = BoolProperty.ConvertValueToBool(pValue);
                this.setGlazingBars(tValue, this.mOpening.OpeningParams);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tGlazingBarsProp);
        //glazing bars width
        let tGlazingBars_WidthProp = new NumberProperty(this.mOpening, Window.PROP_KEY_GLAZINGBARS_WIDTH, this.mRoomManager.resources.propertyGlazingBarsWidth);
        tGlazingBars_WidthProp.class = "glazingbars";
        tGlazingBars_WidthProp.minValue = 0.01;
        tGlazingBars_WidthProp.maxValue = 0.1;
        tGlazingBars_WidthProp.numDecDigits = 2;
        tGlazingBars_WidthProp.numDigits = 3;
        tGlazingBars_WidthProp.visible = this.mOpening.OpeningParams.glazingBars;
        tGlazingBars_WidthProp.getValue = () => this.getGlazingBarsWidth();
        tGlazingBars_WidthProp.setValue = (pValue) => {
            if (pValue != null) {
                let tValue = NumberProperty.ConvertValueToNumber(pValue);
                this.setGlazingBarsWidth(tValue, this.mOpening.OpeningParams);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tGlazingBars_WidthProp);
        //glazing bars horizontal
        let tGlazingBars_HCountProp = new NumberProperty(this.mOpening, Window.PROP_KEY_GLAZINGBARS_HORIZONTAL, this.mRoomManager.resources.propertyGlazingBarsHorizontal);
        tGlazingBars_HCountProp.class = "glazingbars";
        tGlazingBars_HCountProp.minValue = 1;
        tGlazingBars_HCountProp.numDecDigits = 0;
        tGlazingBars_HCountProp.numDigits = 2;
        tGlazingBars_HCountProp.visible = this.mOpening.OpeningParams.glazingBars;
        tGlazingBars_HCountProp.getValue = () => this.getGlazingBarsHorizontal();
        tGlazingBars_HCountProp.setValue = (pValue) => {
            if (pValue != null) {
                let tValue = NumberProperty.ConvertValueToNumber(pValue);
                this.setGlazingBarsHorizontal(tValue, this.mOpening.OpeningParams);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tGlazingBars_HCountProp);
        //glazing bars vertical
        let tGlazingBars_VCountProp = new NumberProperty(this.mOpening, Window.PROP_KEY_GLAZINGBARS_VERTICAL, this.mRoomManager.resources.propertyGlazingBarsVertical);
        tGlazingBars_VCountProp.class = "glazingbars";
        tGlazingBars_VCountProp.minValue = 1;
        tGlazingBars_VCountProp.numDecDigits = 0;
        tGlazingBars_VCountProp.numDigits = 2;
        tGlazingBars_VCountProp.visible = this.mOpening.OpeningParams.glazingBars;
        tGlazingBars_VCountProp.getValue = () => this.getGlazingBarsVertical();
        tGlazingBars_VCountProp.setValue = (pValue) => {
            if (pValue != null) {
                let tValue = NumberProperty.ConvertValueToNumber(pValue);
                this.setGlazingBarsVertical(tValue, this.mOpening.OpeningParams);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tGlazingBars_VCountProp);
        return (tProperties);
    }
    serializeIOData() {
        return (this.mOpening.OpeningParams);
    }
    loadIOData(pData) {
        if (pData == null)
            return;
        let tParams = pData;
        this.setSegments(tParams.ColumnCount, this.mOpening.OpeningParams);
        this.setBlind(tParams.blindVisible, this.mOpening.OpeningParams);
        this.setBlindOpeningAngle(tParams.blindOpeningAngle, this.mOpening.OpeningParams);
        this.setBlindOpeningWidth(tParams.blindOpeningWidth, this.mOpening.OpeningParams);
        this.setPrivacyFilter(tParams.privacyFilter, this.mOpening.OpeningParams);
        this.setWindowSill(tParams.privacyFilter, this.mOpening.OpeningParams);
    }
    createSill(pParent) {
        let tW = this.mOpening.width;
        let tD = this.mOpening.depth;
        let tSill = CreateBox("", {
            width: tW + Window.SILL_WIDTH_OFFSET * 2,
            height: Window.SILL_HEIGHT,
            depth: tD + Window.SILL_DEPTH_OFFSET * 2
        }, this.mScene);
        tSill.material = this.getWhitePlasticMaterial();
        if (this.mOpening.wall != null)
            tSill.name = "room:wall|" + this.mOpening.wall.id.toString();
        tSill.parent = pParent;
        this.prepare3dMesh(tSill);
    }
    createFrame(pParent, pSegmentNumber) {
        let tHeight = this.getFrameHeight();
        let tHW = this.mOpening.width;
        let tSW = tHW * (1 / this.mOpening.OpeningParams.ColumnCount); // segment width
        let tSP = (-(tSW * 0.5) * (this.mOpening.OpeningParams.ColumnCount - 1)) + (tSW * (pSegmentNumber - 1));
        let tFrame = GeoUtils.createFrame(tSW, tHeight, Window.FRAME_DEPTH, Window.FRAME_THICKNESS, pParent, this.mScene);
        if (tFrame != null) {
            tFrame.position.x = tSP;
            tFrame.position.y = this.mOpening.height - tHeight * 0.5;
            tFrame.material = this.getWhitePlasticMaterial();
            this.prepare3dMesh(tFrame);
        }
        if (this.mOpening.wall != null)
            tFrame.name = "room:wall|" + this.mOpening.wall.id.toString();
        if (this.mOpening.OpeningParams.privacyFilter) {
            this.createPrivacyFilter(pParent, true);
        }
        return (tFrame);
    }
    createSash(pFrame, pSegmentNumber) {
        if (pFrame == null)
            return;
        let tFrameHeight = this.getFrameHeight();
        let tSashWidth = this.mOpening.width * (1 / this.mOpening.OpeningParams.ColumnCount) - Window.FRAME_THICKNESS;
        let tSashHeight = tFrameHeight - Window.FRAME_THICKNESS;
        let tPaneWidth = tSashWidth - Window.SASH_THICKNESS * 2;
        let tPaneHeight = tSashHeight - Window.SASH_THICKNESS * 2;
        let tSash = GeoUtils.createFrame_CAG(tSashWidth, tSashHeight, Window.SASH_DEPTH, Window.SASH_THICKNESS, pFrame, true, this.mScene);
        if (tSash == null)
            return;
        tSash.position.z = -0.03;
        if (this.mOpening.mirrorHorizontal)
            tSash.position.z = -tSash.position.z;
        tSash.material = this.mOpening.getMaterialofSet(this.mOpening.MaterialSet_Sash);
        if (this.mOpening.wall != null)
            tSash.name = "room:wall|" + this.mOpening.wall.id.toString();
        tSash.parent = pFrame;
        this.prepare3dMesh(tSash);
        let tPane = CreateBox("", {
            width: tPaneWidth, height: tPaneHeight, depth: 0.01
        }, this.mScene);
        tPane.material = this.getSheetGlassMaterial();
        if (this.mOpening.wall != null)
            tPane.name = "room:wall|" + this.mOpening.wall.id.toString();
        tPane.parent = tSash;
        this.prepare3dMesh(tPane);
        this._createHandle(pFrame, pSegmentNumber);
    }
    mergeMeshes_CSG(pMeshes) {
        if (pMeshes.length == 0) {
            return (null);
        }
        if (pMeshes.length == 1) {
            return (pMeshes[0]);
        }
        let tmergedMesh = CSG.FromMesh(pMeshes[0]);
        for (let i = 1; i < pMeshes.length; i++) {
            const tnextMesh = CSG.FromMesh(pMeshes[i]);
            tmergedMesh = tmergedMesh.union(tnextMesh);
            pMeshes[i].dispose();
        }
        pMeshes[0].dispose();
        const tRes = tmergedMesh.toMesh("mergedBar", null, this.mScene);
        return (tRes);
    }
    createGlazingBarsH(pFrame) {
        if (pFrame == null)
            return (null);
        let tBars = [];
        let tFrameHeight = this.getFrameHeight();
        let tSashWidth = (this.mOpening.width * (1 / this.mOpening.OpeningParams.ColumnCount) - Window.FRAME_THICKNESS);
        let tSashHeight = (tFrameHeight - Window.FRAME_THICKNESS) - Window.SASH_THICKNESS * 2;
        let tGlazingBarsH = Util.CAG_createPlane(tSashWidth - (Window.SASH_THICKNESS * 2), Window.GLAZINGBAR_THICKNESS, this.mOpening.OpeningParams.glazingBars_width, this.mScene);
        if (tGlazingBarsH == null)
            return (null);
        tGlazingBarsH.parent = pFrame;
        tBars.push(tGlazingBarsH);
        for (let i = 1; i < this.mOpening.OpeningParams.glazingBars_h_segments; i++) {
            const tclonedBar = tBars[0].clone();
            tclonedBar.parent = pFrame;
            tBars.push(tclonedBar);
        }
        const tgap = tSashHeight / (this.mOpening.OpeningParams.glazingBars_h_segments + 1);
        const tPosY0 = (-tSashHeight * 0.5) - (this.mOpening.OpeningParams.glazingBars_width * 0.5);
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < tBars.length; i++) {
            tBars[i].position.x = 0;
            tBars[i].position.y = tPosY0 + (tgap * (i + 1));
            tBars[i].position.z = -(Window.FRAME_THICKNESS * 0.5);
            if (this.mOpening.mirrorHorizontal) {
                tBars[i].position.z = -tBars[i].position.z;
            }
        }
        const tmergedBar = this.mergeMeshes_CSG(tBars);
        if (tmergedBar == null)
            return (null);
        tmergedBar.parent = pFrame;
        this.prepare3dMesh(tmergedBar);
        return (tmergedBar);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createGlazingBarsV(pFrame) {
        if (pFrame == null)
            return (null);
        let tBars = [];
        let tFrameHeight = this.getFrameHeight();
        let tSashWidth = (this.mOpening.width * (1 / this.mOpening.OpeningParams.ColumnCount) - Window.FRAME_THICKNESS)
            - Window.SASH_THICKNESS * 2;
        let tSashHeight = tFrameHeight - Window.FRAME_THICKNESS;
        let tGlazingBarsV = Util.CAG_createPlane(this.mOpening.OpeningParams.glazingBars_width, Window.GLAZINGBAR_THICKNESS, tSashHeight - (Window.SASH_THICKNESS * 2), this.mScene);
        if (tGlazingBarsV == null)
            return (null);
        tGlazingBarsV.parent = pFrame;
        tBars.push(tGlazingBarsV);
        for (let i = 1; i < this.mOpening.OpeningParams.glazingBars_v_segments; i++) {
            const tclonedBar = tBars[0].clone();
            tclonedBar.parent = pFrame;
            tBars.push(tclonedBar);
        }
        const tgap = tSashWidth / (this.mOpening.OpeningParams.glazingBars_v_segments + 1);
        const tPosX0 = (-tSashHeight * 0.5) - (this.mOpening.OpeningParams.glazingBars_width * 0.5);
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < tBars.length; i++) {
            tBars[i].position.x = (tgap * (i + 1)) - (tSashWidth * 0.5);
            tBars[i].position.y = (-tSashHeight * 0.5) + Window.SASH_THICKNESS;
            tBars[i].position.z = -(Window.FRAME_THICKNESS * 0.5);
            if (this.mOpening.mirrorHorizontal) {
                tBars[i].position.z = -tBars[i].position.z;
            }
        }
        const tmergedBar = this.mergeMeshes_CSG(tBars);
        if (tmergedBar == null)
            return (null);
        tmergedBar.parent = pFrame;
        this.prepare3dMesh(tmergedBar);
        return (tmergedBar);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _createHandle(pSash, pSegmentNumber) {
        if (pSash == null)
            return;
        let tFrameHeight = this.mOpening.height - (Window.SILL_HEIGHT * 0.5);
        let tSashWidth = this.mOpening.width * (1 / this.mOpening.OpeningParams.ColumnCount) - Window.FRAME_THICKNESS;
        let tBox1 = CreateBox("", { width: 0.03, height: 0.05, depth: 0.01 }, this.mScene);
        let tBox2 = CreateBox("", { width: 0.02, height: 0.02, depth: 0.03 }, this.mScene);
        tBox2.position.z = -0.02;
        if (this.mOpening.mirrorHorizontal)
            tBox2.position.z = -tBox2.position.z;
        let tBox3 = CreateBox("", { width: 0.02, height: 0.13, depth: 0.02 }, this.mScene);
        tBox3.position.y = -0.055;
        if (this.mOpening.mirrorHorizontal)
            tBox3.position.z = tBox2.position.z + 0.015 + 0.01;
        else
            tBox3.position.z = tBox2.position.z - 0.015 - 0.01;
        let tHandle = Mesh.MergeMeshes([tBox1, tBox2, tBox3]);
        tHandle.material = this.getMetalMaterial();
        if (pSegmentNumber == 2) {
            tHandle.position.x = -tSashWidth * 0.5 + Window.SASH_THICKNESS * 0.5;
        }
        else {
            tHandle.position.x = tSashWidth * 0.5 - Window.SASH_THICKNESS * 0.5;
        }
        tHandle.position.y = -tFrameHeight * 0.05;
        tHandle.position.y = -tFrameHeight * 0.05;
        tHandle.position.z = -Window.SASH_DEPTH + 0.005;
        if ((this.mOpening.OpeningParams.ColumnCount != 2) &&
            ((pSegmentNumber > 2) || (pSegmentNumber == 1)) &&
            (this.mOpening.mirrorVertical)) {
            tHandle.position.x = -tHandle.position.x;
        }
        if (this.mOpening.mirrorHorizontal)
            tHandle.position.z = -tHandle.position.z;
        if (this.mOpening.wall != null)
            tHandle.name = "room:wall|" + this.mOpening.wall.id.toString();
        tHandle.parent = pSash;
        this.prepare3dMesh(tHandle);
    }
    getFrameHeight() {
        return (this.mOpening.height - (Window.SILL_HEIGHT * 0.5));
    }
    /** @internal */
    getGlazingBarsWidth() {
        this.mGlazingBarsWidth_Prop.value = NumberProperty.ConvertValueToNumber(this.mOpening.OpeningParams.glazingBars_width);
        this.mGlazingBarsWidth_Prop.text = this.mOpening.OpeningParams.glazingBars_width.toLocaleString();
        return (this.mGlazingBarsWidth_Prop);
    }
    /** @internal */
    getGlazingBarsHorizontal() {
        this.mGlazingBarsHorizontal_Prop.value = NumberProperty.ConvertValueToNumber(this.mOpening.OpeningParams.glazingBars_h_segments);
        this.mGlazingBarsHorizontal_Prop.text = this.mOpening.OpeningParams.glazingBars_h_segments.toLocaleString();
        return (this.mGlazingBarsHorizontal_Prop);
    }
    /** @internal */
    getGlazingBarsVertical() {
        this.mGlazingBarsVertical_Prop.value = NumberProperty.ConvertValueToNumber(this.mOpening.OpeningParams.glazingBars_v_segments);
        this.mGlazingBarsVertical_Prop.text = this.mOpening.OpeningParams.glazingBars_v_segments.toLocaleString();
        return (this.mGlazingBarsVertical_Prop);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Window.SILL_HEIGHT = 0.04;
Window.SILL_DEPTH_OFFSET = 0.05;
Window.SILL_WIDTH_OFFSET = 0.025;
Window.FRAME_DEPTH = 0.06;
Window.FRAME_THICKNESS = 0.06;
Window.SASH_DEPTH = 0.08;
Window.SASH_THICKNESS = 0.08;
Window.GLAZINGBAR_THICKNESS = 0.015;
